import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Box, Button, Card, Group, Switch, Text } from "@mantine/core";
import { useNavigate, useParams, Route, Routes, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  ErrorView,
  PageTitle,
  Subnav,
  AvatarManagement,
  Can,
} from "@components/shared";
import {
  OrganizationForm,
  OrganizationAssessmentInstruments,
  OrganizationOccupationalProfiles,
  OrganizationPermissions,
  OrganizationMemberManagement,
  OrganizationAssignmentRequests,
  OrganizationAssessmentAssign,
  OrganizationMyTeam,
} from "@components/Organization";
import { UserOrgMembership } from "@components/User";
import { AssessmentManagement } from "@components/Assessment";
import AssessmentDetail from "./AssessmentDetail";

import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";
import { PERMISSIONS } from "@util/permissions";
import usePermission from "@util/usePermission";
import { SurveyList } from "@components/Survey";
import { RequestList } from "@components/Request";
import { EvaluationList } from "@components/Evaluation";

export default function OrganizationDetail() {
  const [error, setError] = useState(null);
  const [org, setOrg] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { id } = params;
  const canViewManagedUsers = usePermission(
    PERMISSIONS.ORGANIZATION_MANAGED_USERS_VIEW
  );
  const canSeeAssignmentRequests = usePermission(
    [
      PERMISSIONS.ASSESSMENTS_APPROVE,
      PERMISSIONS.ASSESSMENTS_ORG_APPROVE,
      PERMISSIONS.ASSESSMENTS_REQUEST,
    ],
    { mode: "any" }
  );

  useEffect(() => {
    fetchData();
  }, [id]);

  function fetchData() {
    axios
      .get(`/organizations/${id}/`)
      .then(({ data }) => {
        setOrg(data.response[0]);
      })
      .catch((err) => {
        setError(err);
        setOrg(null);
      });
  }

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!org) return null;

  const subnavLinks = [
    { text: t("detail"), to: `/organizations/${id}` },
    {
      text: t("organization_admin_plural"),
      to: `/organizations/${id}/admins`,
    },
    { text: t("manager_plural"), to: `/organizations/${id}/managers` },
    { text: t("user_plural"), to: `/organizations/${id}/users` },
  ];
  if (canViewManagedUsers) {
    subnavLinks.push({
      text: t("my_team"),
      to: `/organizations/${id}/my-team`,
    });
  }
  subnavLinks.push(
    {
      text: t("assessment_instrument_plural"),
      to: `/organizations/${id}/instruments`,
    },
    {
      text: t("cs:profile_plural"),
      to: `/organizations/${id}/profiles`,
    },
    {
      text: t("assign"),
      to: `/organizations/${id}/assign`,
    }
  );
  if (canSeeAssignmentRequests) {
    subnavLinks.push({
      text: t("assignment_request_plural"),
      to: `/organizations/${id}/assignment-requests`,
    });
  }
  subnavLinks.push(
    {
      text: t("assessment_plural"),
      to: `/organizations/${id}/assessments`,
    },
    { text: t("survey_plural"), to: `/organizations/${id}/surveys` },
    {
      text: t("cs:request_plural"),
      to: `/organizations/${id}/requests`,
    },
    {
      text: t("cs:evaluation_plural"),
      to: `/organizations/${id}/evaluations`,
    }
  );

  return (
    <div>
      <Group>
        <AvatarManagement asset={org.profile_asset} orgId={id} />
        <PageTitle
          title={`${org.name}`}
          subtitle={t("cs:organization_singular")}
          icon={entityIcon.company()}
          iconColor={entityColor.organization}
        />
      </Group>
      <Box mt="md" mb="md">
        <Subnav links={subnavLinks} />
      </Box>
      <Routes>
        <Route
          path="/"
          element={
            <>
              <Card mt="md">
                <OrganizationForm id={id} org={org} onSuccess={fetchData} />
              </Card>
              <Card mt="md">
                <ToggleActive
                  fetchData={fetchData}
                  id={id}
                  isActive={org.status === 1}
                />
              </Card>
              <Card mt="md">
                <OrganizationPermissions
                  organizationId={id}
                  permissionKeys={org.permission_keys || []}
                  onSuccess={fetchData}
                />
              </Card>
              <Can permission={PERMISSIONS.ORGANIZATIONS_DELETE}>
                <Card mt="md">
                  <Text size="sm" c="dimmed" mb="sm">
                    {t("delete_organization_description")}
                  </Text>
                  <Button
                    color="red"
                    variant="light"
                    onClick={() => {
                      axios
                        .delete(`/organizations/${id}/`)
                        .then(() => {
                          toast.success(t("updated"));
                          navigate("/organizations");
                        })
                        .catch((err) => toast.error(err));
                    }}
                  >
                    {t("delete_organization")}
                  </Button>
                </Card>
              </Can>
            </>
          }
        />
        <Route
          path="/admins"
          element={
            <OrganizationMemberManagement
              organizationId={id}
              type="admins"
            />
          }
        />
        <Route
          path="/managers"
          element={
            <OrganizationMemberManagement
              organizationId={id}
              type="managers"
            />
          }
        />
        <Route
          path="/users"
          element={
            <OrganizationMemberManagement organizationId={id} type="users" />
          }
        />
        <Route
          path="/my-team"
          element={<OrganizationMyTeam organizationId={id} />}
        />
        <Route
          path="/users/:userId"
          element={<OrgUserMembershipRoute organizationId={id} />}
        />
        <Route
          path="/users/:userId/roles"
          element={<OrgUserRolesRedirect organizationId={id} />}
        />
        <Route
          path="/instruments"
          element={
            <OrganizationAssessmentInstruments organizationId={id} />
          }
        />
        <Route
          path="/profiles"
          element={
            <Can permission={PERMISSIONS.ORG_OCCUPATIONAL_PROFILES_VIEW}>
              <OrganizationOccupationalProfiles organizationId={id} />
            </Can>
          }
        />
        <Route
          path="/assign"
          element={<OrganizationAssessmentAssign organizationId={id} />}
        />
        <Route
          path="/assignment-requests"
          element={
            <OrganizationAssignmentRequests organizationId={id} />
          }
        />
        <Route
          path="/assessments"
          element={<AssessmentManagement organizationId={id} />}
        />
        <Route
          path="/assessments/:id"
          element={<AssessmentDetail />}
        />
        <Route
          path="/surveys"
          element={
            <SurveyList
              requestInfo={{
                url: `/retrieve-surveys/`,
                data: {
                  organization_id: id,
                },
              }}
            />
          }
        />
        <Route
          path="/requests"
          element={
            <RequestList
              requestInfo={{
                url: `/retrieve-requests/`,
                data: {
                  organization_id: id,
                },
              }}
            />
          }
        />
        <Route
          path="/evaluations"
          element={
            <EvaluationList
              requestInfo={{
                url: `/organizations/${id}/retrieve-assessments/`,
                data: {
                  organization_id: id,
                  activity_status: "scored",
                },
              }}
            />
          }
        />
      </Routes>
    </div>
  );
}

function OrgUserMembershipRoute({ organizationId }) {
  const { userId } = useParams();
  return (
    <UserOrgMembership userId={userId} organizationId={organizationId} />
  );
}

function OrgUserRolesRedirect({ organizationId }) {
  const { userId } = useParams();
  return (
    <Navigate
      to={`/organizations/${organizationId}/users/${userId}`}
      replace
    />
  );
}

function ToggleActive({ fetchData, id, isActive }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  function toggleActive() {
    setLoading(true);
    axios
      .post(`/organizations/${id}/active-toggle/`)
      .then(() => {
        setLoading(false);
        fetchData();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  return (
    <Switch
      label={t("activation_status")}
      checked={!!isActive}
      disabled={loading}
      onChange={() => toggleActive()}
    />
  );
}
